import type { ContentImportCollection, ContentImportItemDto } from "./types";
import {
  QUESTION_DIFFICULTIES,
  LEVEL_MODALITIES,
  LEVEL_PLAY_FORMATS,
  PRIZE_AMOUNTS,
  UNIT_UNLOCK_MODES,
  levelOmitsPlayFormat,
} from "./enums";
import { validateQuestionOptionsImport } from "./validateQuestionOptions";
import {
  fail,
  mergeResults,
  ok,
  optionalString,
  rejectInternalId,
  requireEnum,
  requireLocalizedString,
  requireNonEmptyString,
  requireNumberInRange,
  type ValidationResult,
} from "./validationHelpers";

function validateQuestionDifficulties(value: unknown): ValidationResult {
  if (value === undefined) {
    return ok;
  }
  if (!Array.isArray(value)) {
    return fail("questionDifficulties must be an array");
  }
  for (let i = 0; i < value.length; i++) {
    const result = requireEnum(
      value[i],
      `questionDifficulties[${i}]`,
      QUESTION_DIFFICULTIES
    );
    if (!result.ok) {
      return result;
    }
  }
  return ok;
}

export function validateUnitImportItem(item: ContentImportItemDto): ValidationResult {
  return mergeResults(
    rejectInternalId(item),
    requireNonEmptyString(item.code, "code"),
    requireLocalizedString(item.name, "name"),
    requireEnum(item.unlockMode, "unlockMode", UNIT_UNLOCK_MODES)
  );
}

export function validateQuestionImportItem(
  item: ContentImportItemDto
): ValidationResult {
  if ("correctOptionCode" in item || "correctOptionIdentifier" in item) {
    return fail(
      "correctOptionCode / correctOptionIdentifier are not supported; set isCorrect on each option"
    );
  }

  return mergeResults(
    rejectInternalId(item),
    requireNonEmptyString(item.code, "code"),
    requireNonEmptyString(item.unitCode, "unitCode"),
    requireLocalizedString(item.question, "question"),
    requireLocalizedString(item.feedbackCorrect, "feedbackCorrect"),
    requireLocalizedString(item.feedbackIncorrect, "feedbackIncorrect"),
    requireEnum(item.difficulty, "difficulty", QUESTION_DIFFICULTIES),
    validateQuestionOptionsImport(item.options),
    item.hint !== undefined
      ? requireLocalizedString(item.hint, "hint")
      : ok
  );
}

export function validateStoryImportItem(item: ContentImportItemDto): ValidationResult {
  return mergeResults(
    rejectInternalId(item),
    requireNonEmptyString(item.code, "code"),
    requireNonEmptyString(item.unitCode, "unitCode"),
    requireLocalizedString(item.title, "title"),
    requireLocalizedString(item.body, "body"),
    optionalString(item.video, "video")
  );
}

function validateLevelPrizeAmount(item: ContentImportItemDto): ValidationResult {
  if (item.modality === "LEVEL_MODALITY_STORY") {
    if (item.prizeAmount == null || item.prizeAmount === "") {
      return ok;
    }
    return requireEnum(item.prizeAmount, "prizeAmount", ["PRIZE_AMOUNT_NONE"]);
  }
  return requireEnum(item.prizeAmount, "prizeAmount", PRIZE_AMOUNTS);
}

export function validateLevelImportItem(
  item: ContentImportItemDto
): ValidationResult {
  const base = mergeResults(
    rejectInternalId(item),
    requireNonEmptyString(item.code, "code"),
    requireNonEmptyString(item.unitCode, "unitCode"),
    requireNumberInRange(item.order, "order", 1, 9999),
    requireEnum(item.modality, "modality", LEVEL_MODALITIES),
    levelOmitsPlayFormat(item.modality)
      ? ok
      : requireEnum(item.playFormat, "playFormat", LEVEL_PLAY_FORMATS),
    validateLevelPrizeAmount(item)
  );
  if (!base.ok) {
    return base;
  }

  if (item.modality === "LEVEL_MODALITY_STORY") {
    return mergeResults(
      base,
      requireNonEmptyString(item.storyCode, "storyCode")
    );
  }

  if (
    item.modality === "LEVEL_MODALITY_MAZE" ||
    item.modality === "LEVEL_MODALITY_REWARD"
  ) {
    return base;
  }

  return mergeResults(base, validateQuestionDifficulties(item.questionDifficulties));
}

export function validateImportItem(
  collection: ContentImportCollection,
  item: ContentImportItemDto
): ValidationResult {
  switch (collection) {
    case "units":
      return validateUnitImportItem(item);
    case "questions":
      return validateQuestionImportItem(item);
    case "stories":
      return validateStoryImportItem(item);
    case "levels":
      return validateLevelImportItem(item);
  }
}

export function importItemKey(
  collection: ContentImportCollection,
  item: ContentImportItemDto
): string {
  return String(item.code ?? "?");
}
